// Issue keys and states as the markdown renderer sees them. The vocabulary is
// the contract's (docs/architecture/board-design.md, "Vocabulary"). A key is
// read and written by lib/keys.ts and nowhere here.
import { keysIn, spellKey, type KeyMatch } from '../lib/keys'


/**
 * State slug → display name, in board order. Approve, Deployed and Completed
 * are no longer states (Poulpe-183) but old text and history still name them.
 */
export const STATE_NAMES: Readonly<Record<string, string>> = {
  backlog: 'Backlog',
  todo: 'Todo',
  in_progress: 'In Progress',
  on_main: 'On Main',
  done: 'Done',
  cancelled: 'Cancelled',
  duplicate: 'Duplicate',
  approve: 'Approve',
  deployed: 'Deployed',
  completed: 'Completed',
}

/** The display name of a state; an unknown slug is shown humanised. */
export function stateName(slug: string): string {
  const known = STATE_NAMES[slug]
  if (known) return known
  return slug
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

export type { KeyMatch } from '../lib/keys'

/**
 * Every key of these projects in `text` that stands on its own, in any
 * spelling (`sun-12`, `SUN-0012`), each as it is written (`SUN-012`) and with
 * how much of the text spelled it (lib/keys.ts, `keysIn`).
 */
export function findIssueKeys(text: string, prefixes: readonly string[] = []): KeyMatch[] {
  return keysIn(text, prefixes)
}

/** The key, written the one way, when `text` is exactly one key of these projects, else null. */
export function exactIssueKey(text: string, prefixes: readonly string[] = []): string | null {
  return spellKey(text, prefixes)
}

/**
 * The issue a link points at, as its key is written, or null. Recognised: the
 * app's own relative `/SUN-012` (also `/link/SUN-012`), and absolute URLs
 * whose path ends with the key (`https://l.poulpe.dev/SUN-012`,
 * `…/link/sun-12`) or names it after an `issue` segment
 * (`https://linear.app/<team>/issue/SUN-12/<slug>`), in any spelling.
 */
export function issueKeyFromHref(href: string | null | undefined, prefixes: readonly string[] = []): string | null {
  if (!href) return null
  let path: string
  if (href.startsWith('/') && !href.startsWith('//')) {
    path = href
  } else if (/^https?:\/\//i.test(href)) {
    try {
      path = new URL(href).pathname
    } catch {
      return null
    }
  } else {
    return null
  }
  const segments = path
    .replace(/[?#].*$/, '')
    .split('/')
    .filter(Boolean)
    .map((segment) => {
      try {
        return decodeURIComponent(segment)
      } catch {
        return segment
      }
    })
  if (segments.length === 0) return null
  const last = exactIssueKey(segments[segments.length - 1]!, prefixes)
  if (last) return last
  const issueAt = segments.findIndex((segment) => segment.toLowerCase() === 'issue')
  if (issueAt >= 0 && issueAt + 1 < segments.length) return exactIssueKey(segments[issueAt + 1]!, prefixes)
  return null
}

/** Keys referenced anywhere in a source text (a superset: code included). */
export function collectIssueKeys(source: string, prefixes: readonly string[] = []): string[] {
  const seen = new Set<string>()
  for (const { key } of findIssueKeys(source, prefixes)) seen.add(key)
  return [...seen]
}
